import { useUserReceipts } from "@/hooks/payments/usePaymentReceipts";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Clock, CheckCircle, XCircle, ExternalLink } from "lucide-react";

interface PendingReceiptsProps {
  userId: string | undefined;
}

const statusConfig = {
  pending: {
    label: "Pendiente",
    icon: Clock,
    color: "text-yellow-600 dark:text-yellow-400",
    bg: "bg-yellow-500/10",
  },
  approved: {
    label: "Aprobado",
    icon: CheckCircle,
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-500/10",
  },
  rejected: {
    label: "Rechazado",
    icon: XCircle,
    color: "text-red-600 dark:text-red-400",
    bg: "bg-red-500/10",
  },
};

export const PendingReceipts = ({ userId }: PendingReceiptsProps) => {
  const { data: receipts, isLoading } = useUserReceipts(userId);

  if (isLoading || !receipts?.length) return null;

  return (
    <div className="bg-primary/10 dark:bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-black/10 dark:border-white/10">
      <h3 className="text-xl font-heading mb-4 text-foreground">Mis Comprobantes</h3>
      <div className="space-y-3">
        {receipts.map((receipt) => {
          const config = statusConfig[receipt.status as keyof typeof statusConfig] || statusConfig.pending;
          const Icon = config.icon;
          return (
            <div
              key={receipt.id}
              className="p-4 bg-black/5 dark:bg-white/5 rounded-lg border border-black/10 dark:border-white/10"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium text-sm text-foreground">
                    {receipt.category_name || "Sin categoría"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(receipt.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                  </p>
                  {receipt.status === "rejected" && receipt.rejection_reason && (
                    <p className="text-xs text-red-500 dark:text-red-400">
                      Motivo: {receipt.rejection_reason}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={receipt.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
